import { useEffect, useRef } from "react";
import { Suit, type Card, type ShTableState } from "../gen/bazivan/v1/table_pb";
import { signal, useSignal, type Signal } from "../net/signal";
import { CardView } from "../ui/Card";
import { HokmScene } from "../hokm/scene";
import { ShelemOnline } from "../shelem/controller";
import { bidSteps } from "../shelem/state";
import { Overlay } from "../ui/Overlay";
import { MatchOver } from "./MatchOver";
import { unitName } from "./watching";
import { fa, suitSymbol } from "./cards";

/**
 * میزِ شلم — ظرفِ ری‌اکتیِ صحنه‌ی امری.
 *
 * صحنه همان HokmScene است ([[asHokmView]]) و کرومِ دورِ میز این‌جاست. دو تکه‌ی
 * آن تازه‌اند و در حکم و چهاربرگ همتایی ندارند: **انتخابگرِ مزایده** و
 * **نوارِ واگذاری**.
 */
export function ShelemTable({ state }: { state: ShTableState }) {
  const host = useRef<HTMLDivElement>(null);
  const sig = useRef<Signal<ShelemOnline | null> | null>(null);
  if (!sig.current) sig.current = signal<ShelemOnline | null>(null);

  useEffect(() => {
    const el = host.current;
    if (!el) return;
    let c: ShelemOnline | null = null;
    const scene = new HokmScene(el, state.seats.length || 4, {
      onCard: (i) => c?.clickCard(i),
      seatName: (l) => c?.seatName(l) ?? "حریف",
    });
    c = new ShelemOnline(scene);
    sig.current!.value = c;
    c.start();
    const ro = new ResizeObserver(() => scene.relayout());
    ro.observe(el);
    return () => { ro.disconnect(); c?.dispose(); scene.destroy(); sig.current!.value = null; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="table tv-host hkm" ref={host}>
      <Chrome sig={sig.current} />
    </div>
  );
}

function Chrome({ sig }: { sig: Signal<ShelemOnline | null> }) {
  const c = useSignal(sig);
  if (!c) return null;
  return <ChromeInner c={c} />;
}

function ChromeInner({ c }: { c: ShelemOnline }) {
  const overlay = useSignal(c.overlay);
  const picked = useSignal(c.picked);
  const widow = useSignal(c.widow);
  useSignal(c.raw); // هر وضعیتِ تازه این کروم را هم می‌کشد
  const st = c.state;
  const mine = (u: number) => !st.watching && u === 0;

  return (
    <>
      <ShScorebar c={c} />

      {st.awaitingBidFromMe && <BidPicker c={c} />}
      {st.awaitingDiscardFromMe && <DiscardBar c={c} picked={picked} widow={widow} />}
      {st.awaitingTrumpFromMe && <TrumpPicker c={c} />}

      {overlay?.kind === "hand" && (
        <Overlay label="پایانِ دست" onClose={() => c.nextHand()}>
          <h2>{overlay.shelem ? "شلم!" : overlay.yasa ? "یاسا" : overlay.kot ? "کُت" : "پایانِ دست"}</h2>
          <p className="sub">
            حاکم {fa(overlay.bid)} خوانده بود
          </p>
          <table className="res">
            <tbody>
              {overlay.roundScore.map((n, u) => (
                <tr key={u} className={mine(u) ? "me" : ""}>
                  <td>{unitName(u, st.watching)}</td>
                  <td className={n < 0 ? "loss" : "gain"}>{n < 0 ? "−" : "+"}{fa(Math.abs(n))}</td>
                  <td>{fa(st.score[u] ?? 0)}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <button className="btn" onClick={() => c.nextHand()}>دستِ بعد</button>
        </Overlay>
      )}

      {overlay?.kind === "match" && (
        <MatchOver score={st.score} me={0} units={2} watching={st.watching} />
      )}
    </>
  );
}

/**
 * نوارِ بالا: امتیازِ مسابقه، و وقتی حاکم معلوم شد، تعهد و آنچه تا حالا
 * جمع شده. بی این، بازیکن نمی‌داند چقدر تا رسیدن به قولش مانده.
 */
function ShScorebar({ c }: { c: ShelemOnline }) {
  const st = c.state;
  const declared = st.declarer >= 0;
  return (
    <div className="scorebar">
      <span className="pill">
        <b>{fa(st.score[0] ?? 0)}</b> {unitName(0, st.watching)}
      </span>
      <span className="pill">
        <b>{fa(st.score[1] ?? 0)}</b> {unitName(1, st.watching)}
      </span>
      {declared && (
        <span className="pill" title="تعهدِ حاکم و امتیازی که تا حالا جمع کرده">
          تعهد <b>{fa(st.bid)}</b> · جمع <b>{fa(st.collected[st.declarer % 2] ?? 0)}</b>
        </span>
      )}
      <span className="pill muted">هدف {fa(st.targetScore)}</span>
    </div>
  );
}

/**
 * انتخابگرِ مزایده.
 *
 * فقط پله‌های خواندنی نشان داده می‌شوند (از کفِ جاری تا ۱۶۵) — دکمه‌ی مرده
 * نمی‌گذاریم تا بازیکن عددی را امتحان کند که سرور ردش می‌کند. «پاس» هم سرِ
 * اجبارِ شروع‌کننده اصلاً نمی‌آید، چون نبودنش گویاتر از خاموش‌بودنش است.
 */
function BidPicker({ c }: { c: ShelemOnline }) {
  const st = c.state;
  const steps = bidSteps(st);
  return (
    <div className="shbid">
      <div className="head">
        {st.mustBid ? "شروع‌کننده‌ای — باید بخوانی" : "بخوان یا پاس بده"}
        {st.highSeat >= 0 && (
          <span className="muted"> · بالاترین: {fa(st.highBid)} ({c.seatName(st.highSeat)})</span>
        )}
      </div>
      <div className="steps">
        {steps.map((v) => (
          <button key={v} className="btn sm" onClick={() => c.bid(v)}>{fa(v)}</button>
        ))}
        {steps.length === 0 && <span className="muted">بالاتر از ۱۶۵ خواندنی نیست</span>}
      </div>
      {!st.mustBid && <button className="btn pass" onClick={() => c.pass()}>پاس</button>}
    </div>
  );
}

/**
 * نوارِ واگذاری: چهار برگ از شانزده.
 *
 * برگ‌های زمین جدا نشان داده می‌شوند تا حاکم بداند چه گیرش آمده — بی آن، چهار
 * برگ لای دوازده‌تای دیگر گم می‌شد و نمی‌فهمید مزایده چه به او داده. خودِ
 * انتخاب روی دستِ صحنه انجام می‌شود (لمسِ کارت)، و این‌جا فقط شمارش و تأیید.
 */
function DiscardBar({ c, picked, widow }: { c: ShelemOnline; picked: Card[]; widow: Card[] | null }) {
  return (
    <div className="shdiscard">
      {widow && (
        <div className="widow">
          <span className="muted">از زمین:</span>
          {widow.map((card, i) => <CardView key={i} card={card} width={46} />)}
        </div>
      )}
      <div className="head">چهار برگ برای واگذاری بردار — {fa(picked.length)} از ۴</div>
      <div className="picked">
        {picked.map((card, i) => <CardView key={i} card={card} width={46} />)}
      </div>
      <button className="btn" disabled={picked.length !== 4} onClick={() => c.discard()}>واگذار کن</button>
    </div>
  );
}

/** انتخابگرِ حکم — همان چهار خال، مثلِ حکم. */
function TrumpPicker({ c }: { c: ShelemOnline }) {
  const suits = [Suit.HEARTS, Suit.DIAMONDS, Suit.SPADES, Suit.CLUBS];
  return (
    <div className="shtrump">
      <div className="head">حکم را بخوان</div>
      <div className="suits">
        {suits.map((s) => (
          <button key={s} className={`btn suit ${s === Suit.HEARTS || s === Suit.DIAMONDS ? "red" : ""}`}
            onClick={() => c.callTrump(s)}>
            {suitSymbol[s]}
          </button>
        ))}
      </div>
    </div>
  );
}
